import React, { useState } from "react";
import { Dumbbell, Plus, Trash2, Pencil, X, Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { PlayerSelect, type PlayerOption } from "@/components/PlayerSelect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { toast } from "react-toastify";
import { cn } from "@/lib/utils";

interface Stat { id: number; label: string; value: number; order: number }
interface Rec  { id: number; label: string; value: string; order: number }
interface QuizPlayer {
  id: number;
  name: string;
  photo: string | null;
  position: string;
  isActive: boolean;
  nation: { code: string } | null;
  club: { name: string } | null;
  stats: Stat[];
  records: Rec[];
}

type StatForm = { label: string; value: string };
type RecForm  = { label: string; value: string };

const PAGE_SIZE = 15;

const DEFAULT_STATS: StatForm[] = [
  { label: "Goals", value: "" },
  { label: "Assists", value: "" },
  { label: "Appearances", value: "" },
];

const emptyForm = {
  playerId: 0,
  playerName: "",
  playerImageUrl: "",
  stats: DEFAULT_STATS as StatForm[],
  records: [{ label: "", value: "" }] as RecForm[],
};
type FormState = typeof emptyForm;

export default function QuizPlayersPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<QuizPlayer | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<{ playerId?: string; stats?: string; records?: string }>({});
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery<QuizPlayer[]>({
    queryKey: ["quiz", "players"],
    queryFn: async () => {
      const r = (await apiClient.get(API_ENDPOINTS.QUIZ.PLAYERS_LIST)) as unknown as QuizPlayer[] | null;
      return r ?? [];
    },
  });
  const players = data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["quiz", "players"] });

  const { mutate: create, isPending: isCreating } = useMutation({
    mutationFn: (d: object) => apiClient.post(API_ENDPOINTS.QUIZ.PLAYERS_CREATE, d),
    onSuccess: () => { toast.success("Player added"); invalidate(); closeDialog(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed"),
  });

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, d }: { id: number; d: object }) =>
      apiClient.patch(API_ENDPOINTS.QUIZ.PLAYERS_UPDATE(id), d),
    onSuccess: () => { toast.success("Updated"); invalidate(); closeDialog(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed"),
  });

  const { mutate: remove } = useMutation({
    mutationFn: (id: number) => apiClient.delete(API_ENDPOINTS.QUIZ.PLAYERS_DELETE(id)),
    onSuccess: () => { toast.success("Deleted"); invalidate(); },
  });

  const { mutate: toggle } = useMutation({
    mutationFn: (id: number) => apiClient.patch(API_ENDPOINTS.QUIZ.PLAYERS_TOGGLE(id)),
    onSuccess: () => invalidate(),
    onError: () => toast.error("Toggle failed"),
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, stats: DEFAULT_STATS.map((s) => ({ ...s })), records: [{ label: "", value: "" }] });
    setOpen(true);
  };
  const openEdit = (p: QuizPlayer) => {
    setEditing(p);
    setForm({
      playerId: p.id,
      playerName: p.name,
      playerImageUrl: p.photo ?? "",
      stats: p.stats.length
        ? p.stats.map((s) => ({ label: s.label, value: String(s.value) }))
        : DEFAULT_STATS.map((s) => ({ ...s })),
      records: p.records.length
        ? p.records.map((r) => ({ label: r.label, value: r.value }))
        : [{ label: "", value: "" }],
    });
    setOpen(true);
  };
  const closeDialog = () => { setOpen(false); setEditing(null); setErrors({}); };

  const handlePlayerChange = (p: PlayerOption | null) => {
    setForm((f) => ({
      ...f,
      playerId: p?.id ?? 0,
      playerName: p?.name ?? "",
      playerImageUrl: p?.imageUrl ?? "",
    }));
    if (p) setErrors((er) => ({ ...er, playerId: undefined }));
  };

  const buildPayload = () => ({
    playerId: form.playerId,
    stats: form.stats
      .filter((s) => s.label.trim())
      .map((s, i) => ({ label: s.label.trim(), value: Number(s.value), order: i })),
    records: form.records
      .filter((r) => r.label.trim())
      .map((r, i) => ({ label: r.label.trim(), value: r.value.trim(), order: i })),
  });

  const handleSave = () => {
    const newErrors: typeof errors = {};
    if (!form.playerId) newErrors.playerId = "Please select a player";

    const filledStats = form.stats.filter((s) => s.label.trim());
    if (filledStats.length === 0) {
      newErrors.stats = "Add at least one stat (e.g. Goals, Assists)";
    } else {
      for (const s of filledStats) {
        if (s.value.trim() === "") { newErrors.stats = `Stat "${s.label}" is missing a value`; break; }
        const n = Number(s.value);
        if (isNaN(n)) { newErrors.stats = `Stat "${s.label}" must be a number`; break; }
        if (n < 0) { newErrors.stats = `Stat "${s.label}" cannot be negative`; break; }
      }
    }

    const filledRecs = form.records.filter((r) => r.label.trim());
    for (const r of filledRecs) {
      if (r.value.trim() === "") { newErrors.records = `Record "${r.label}" is missing a value`; break; }
    }

    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    setErrors({});

    const payload = buildPayload();
    if (editing) update({ id: editing.id, d: payload });
    else create(payload);
  };

  const setStat = (i: number, k: keyof StatForm, v: string) =>
    setForm((f) => { const stats = [...f.stats]; stats[i] = { ...stats[i], [k]: v }; return { ...f, stats }; });
  const addStat = () => setForm((f) => ({ ...f, stats: [...f.stats, { label: "", value: "" }] }));
  const removeStat = (i: number) => setForm((f) => ({ ...f, stats: f.stats.filter((_, idx) => idx !== i) }));

  const setRec = (i: number, k: keyof RecForm, v: string) =>
    setForm((f) => { const records = [...f.records]; records[i] = { ...records[i], [k]: v }; return { ...f, records }; });
  const addRec = () => setForm((f) => ({ ...f, records: [...f.records, { label: "", value: "" }] }));
  const removeRec = (i: number) => setForm((f) => ({ ...f, records: f.records.filter((_, idx) => idx !== i) }));

  const filtered = players.filter((p) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      p.name.toLowerCase().includes(s) ||
      p.club?.name?.toLowerCase().includes(s) ||
      p.nation?.code?.toLowerCase().includes(s) ||
      p.position?.toLowerCase().includes(s)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleSearch = (v: string) => { setSearch(v); setPage(1); };

  return (
    <Layout>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Dumbbell className="h-8 w-8 text-violet-500" />
            Guess The Player
          </h1>
          <p className="text-gray-500 mt-1">
            {players.length} players · {players.filter((p) => p.isActive).length} active
          </p>
        </div>
        <Button onClick={openCreate} className="bg-violet-600 hover:bg-violet-700 text-white">
          <Plus className="h-4 w-4 mr-2" />
          Add Player
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-xs mb-4">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search by name, club, position…"
          className="pl-8 h-9 text-sm"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Dumbbell className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">{search ? "No matches" : "No players yet"}</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <th className="text-left px-4 py-2.5 font-medium">Player</th>
                  <th className="text-left px-4 py-2.5 font-medium">Nat</th>
                  <th className="text-left px-4 py-2.5 font-medium">Club</th>
                  <th className="text-left px-4 py-2.5 font-medium">Pos</th>
                  <th className="text-left px-4 py-2.5 font-medium">Stats</th>
                  <th className="text-left px-4 py-2.5 font-medium">Records</th>
                  <th className="text-left px-4 py-2.5 font-medium">Active</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paged.map((p) => (
                  <React.Fragment key={p.id}>
                    <tr
                      className={cn("hover:bg-gray-50 transition-colors", !p.isActive && "opacity-50")}
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {p.photo ? (
                            <img src={p.photo} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-gray-100 shrink-0" />
                          )}
                          <span className="font-medium text-gray-900">{p.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-500">{p.nation?.code ?? "—"}</td>
                      <td className="px-4 py-2.5 text-gray-500 max-w-[130px] truncate">{p.club?.name ?? "—"}</td>
                      <td className="px-4 py-2.5 text-gray-500">{p.position}</td>
                      <td className="px-4 py-2.5 text-gray-500">{p.stats.length}</td>
                      <td className="px-4 py-2.5 text-gray-500">{p.records.length}</td>
                      <td className="px-4 py-2.5">
                        <Switch
                          checked={p.isActive}
                          onCheckedChange={() => toggle(p.id)}
                          className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-red-400"
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1 justify-end">
                          <Button
                            variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}
                            title="Preview stats & records"
                          >
                            {expandedId === p.id
                              ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
                              : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(p)}>
                            <Pencil className="h-3.5 w-3.5 text-gray-400" />
                          </Button>
                          <Button
                            variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={() => { if (confirm(`Delete ${p.name}?`)) remove(p.id); }}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-red-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>

                    {expandedId === p.id && (
                      <tr className="bg-gray-50">
                        <td colSpan={8} className="px-6 py-3">
                          <div className="grid grid-cols-2 gap-6 text-sm">
                            <div>
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Stats</p>
                              {p.stats.length === 0 ? (
                                <p className="text-xs text-gray-400">No stats</p>
                              ) : (
                                <div className="space-y-0.5">
                                  {p.stats.map((s) => (
                                    <div key={s.id} className="flex justify-between">
                                      <span className="text-gray-600">{s.label}</span>
                                      <span className="font-semibold text-gray-800">{s.value}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Records</p>
                              {p.records.length === 0 ? (
                                <p className="text-xs text-gray-400">No records</p>
                              ) : (
                                <div className="space-y-0.5">
                                  {p.records.map((r) => (
                                    <div key={r.id}>
                                      <span className="text-gray-500 text-xs">{r.label}: </span>
                                      <span className="text-gray-800 text-xs font-medium">{r.value}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
              <span>
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline" size="sm" className="h-8 w-8 p-0"
                  disabled={safePage === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((n) => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
                  .reduce<(number | "…")[]>((acc, n, idx, arr) => {
                    if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push("…");
                    acc.push(n);
                    return acc;
                  }, [])
                  .map((n, i) =>
                    n === "…" ? (
                      <span key={`ellipsis-${i}`} className="px-1">…</span>
                    ) : (
                      <Button
                        key={n}
                        variant={n === safePage ? "default" : "outline"}
                        size="sm"
                        className={cn("h-8 w-8 p-0", n === safePage && "bg-violet-600 hover:bg-violet-700 text-white")}
                        onClick={() => setPage(n as number)}
                      >
                        {n}
                      </Button>
                    )
                  )}
                <Button
                  variant="outline" size="sm" className="h-8 w-8 p-0"
                  disabled={safePage === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit: ${editing.name}` : "Add Quiz Player"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Player picker */}
            <div>
              <Label className="mb-1 block text-sm">Player <span className="text-red-500">*</span></Label>
              <PlayerSelect
                value={form.playerName ? { name: form.playerName, imageUrl: form.playerImageUrl } : null}
                onChange={handlePlayerChange}
                disabled={!!editing}
              />
              {errors.playerId
                ? <p className="text-xs text-red-500 mt-1 font-medium">{errors.playerId}</p>
                : editing && <p className="text-xs text-gray-400 mt-1">Player cannot be changed — delete and re-add instead.</p>
              }
            </div>

            {/* Stats */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Stats <span className="text-red-500">*</span></Label>
                <button onClick={() => { addStat(); setErrors((er) => ({ ...er, stats: undefined })); }} className="text-xs text-violet-600 hover:underline">+ Add stat</button>
              </div>
              <div className="space-y-2">
                {form.stats.map((s, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input
                      value={s.label}
                      onChange={(e) => { setStat(i, "label", e.target.value); setErrors((er) => ({ ...er, stats: undefined })); }}
                      placeholder="e.g. Goals"
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      value={s.value}
                      onChange={(e) => { setStat(i, "value", e.target.value); setErrors((er) => ({ ...er, stats: undefined })); }}
                      placeholder="0"
                      className="w-24"
                    />
                    {form.stats.length > 1 && (
                      <button onClick={() => removeStat(i)}>
                        <X className="h-4 w-4 text-gray-400 hover:text-red-500" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {errors.stats && <p className="text-xs text-red-500 mt-1.5 font-medium">{errors.stats}</p>}
            </div>

            {/* Records */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Records</Label>
                <button onClick={addRec} className="text-xs text-violet-600 hover:underline">+ Add record</button>
              </div>
              <div className="space-y-2">
                {form.records.map((r, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input
                      value={r.label}
                      onChange={(e) => { setRec(i, "label", e.target.value); setErrors((er) => ({ ...er, records: undefined })); }}
                      placeholder="e.g. Most goals in a season"
                      className="flex-1"
                    />
                    <Input
                      value={r.value}
                      onChange={(e) => { setRec(i, "value", e.target.value); setErrors((er) => ({ ...er, records: undefined })); }}
                      placeholder="e.g. 50 (2011/12)"
                      className="w-40"
                    />
                    {form.records.length > 1 && (
                      <button onClick={() => removeRec(i)}>
                        <X className="h-4 w-4 text-gray-400 hover:text-red-500" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {errors.records && <p className="text-xs text-red-500 mt-1.5 font-medium">{errors.records}</p>}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={isCreating || isUpdating}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              {isCreating || isUpdating ? "Saving…" : editing ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
